// Compute the time-precise lockout timestamp for a NEW pool at creation.
//
// Consumed by src/lib/db.js createGroup (wires this into
// create_group_atomic's p_lockout_at parameter — migration 040). Once set,
// lockout_at is IMMUTABLE (migration 040 trigger, NULL→non-NULL allowed
// exactly once). So this function fires exactly once per pool, at
// creation, before the RPC call.
//
// ── Failure posture ─────────────────────────────────────────────────
// This lookup MUST NEVER hard-fail pool creation. Every branch is wrapped
// in try/catch and returns null on ANY error, timeout, missing data, or
// ambiguity. When we return null, createGroup passes p_lockout_at=null →
// RPC's DEFAULT NULL takes over → pool ships with lockout_at=NULL, same
// as today, and the loose date-only fallback (server checkout endpoint +
// unlock_group RPC) plus the client-side lockoutPassed gate (App.jsx
// handleUnlock, commit 806a197) still cover the common misuse.
//
// A WRONG timestamp is much worse than null. So on the golf branch — the
// only one that requires TZ conversion — we bail if pairings are empty
// OR if tzOffsetHours smells like a server fallback (=== 0). PGA venues
// are never at UTC+0 in practice, so tz=0 signals the server returned
// its ?? 0 fallback for missing meta rather than a real value.
//
// ── Per-sport lookup paths ──────────────────────────────────────────
//   Golf   — fetchGolfPairings(eventId, 1) → walk pairings for min
//            teeTime string, convert to UTC via parseTeeTimeMs using the
//            bundle's tzOffsetHours. Never returns a guessed value.
//   UFC    — getUFCFights(eventId) → min startTimeMs across per-fight
//            objects (earliest = prelim/main-card opener).
//   Daily/ — getGamesByDate(sport, start_date) → min startTimeMs across
//   Weekly  the day's slate. For weekly pools we use start_date (week's
//            first day) as the source day; slightly-late cutoff possible
//            if the earliest game of the week is on a later day.
//            Acceptable degradation — client gate covers subsequent days
//            via the start_date < today (UTC) fallback path.
//   Bracket — /api/espn/:sport/allplayoffs → filter for R1 abbreviation
//            (RD16 for NBA/NHL, WC for MLB/NFL — mirrors
//            getEarliestPlayoffStart in server/index.js:4290), min game
//            date across R1 matchups.
//   Season / rolling / anything else → null (no meaningful single
//            cutoff at creation).

import {
  fetchGolfPairings,
  getUFCFights,
  getGamesByDate,
} from './odds'
import { parseTeeTimeMs } from './golfPickWindow'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// R1 abbreviations per sport — mirrors server/index.js:4290
// getEarliestPlayoffStart's R1_ABBRS_BY_SPORT. Keep in sync.
const R1_ABBRS_BY_SPORT = {
  NBA: ['RD16'],
  NHL: ['RD16'],
  MLB: ['WC'],
  NFL: ['WC'],
}

// Sport → URL path segment on /api/espn/:sport/allplayoffs. Matches the
// scoreboardPaths map at server/index.js:1751.
const PLAYOFF_SPORT_KEY = {
  NBA: 'nba',
  NHL: 'nhl',
  MLB: 'mlb',
  NFL: 'nfl',
}

const FETCH_TIMEOUT_MS = 5000

/**
 * @param {{
 *   sport?: string,
 *   contest_type?: string,
 *   golf_config?: { eventId?: string|number } | null,
 *   ufc_config?:  { eventId?: string|number } | null,
 *   start_date?:  string,     // YYYY-MM-DD
 *   duration_type?: string,   // 'daily' | 'weekly' | 'season' | 'playoffs' | ...
 * }} config
 * @returns {Promise<Date | null>}
 */
export async function computePoolLockout(config) {
  try {
    if (!config || !config.sport) return null

    // Golf — divisions AND pick_em both key on golf_config.eventId. The
    // event-start is round-1 first tee time.
    if (config.sport === 'Golf' && config.golf_config?.eventId) {
      return await computeGolfLockout(config.golf_config.eventId)
    }
    // UFC — event id already threaded into ufc_config at create time.
    if (config.sport === 'UFC' && config.ufc_config?.eventId) {
      return await computeUfcLockout(config.ufc_config.eventId)
    }
    // Bracket — sport-driven R1 lookup via ESPN allplayoffs.
    if (config.contest_type === 'bracket') {
      return await computeBracketLockout(config.sport)
    }
    // Daily/weekly pick'em (excl. Golf/UFC handled above).
    if (config.contest_type === 'pick_em'
        && (config.duration_type === 'daily' || config.duration_type === 'weekly')
        && config.start_date) {
      return await computeDailyOrWeeklyPickemLockout(config.sport, config.start_date)
    }
    // Season / rolling / anything else — no meaningful single cutoff.
    return null
  } catch (err) {
    // Top-level catch — never let this break creation. Log for
    // diagnostics but return null so the RPC call proceeds normally.
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[computePoolLockout] error:', err?.message || err)
    }
    return null
  }
}

// ── Golf ───────────────────────────────────────────────────────────────
async function computeGolfLockout(eventId) {
  try {
    const bundle = await fetchGolfPairings(eventId, 1)
    // Empty pairings → server hasn't published R1 yet (common weeks
    // before tournament week) OR the fetch failed and defaulted to the
    // empty bundle. Either way, bail — we can't trust tzOffsetHours=0
    // in the fallback bundle.
    if (!bundle || !Array.isArray(bundle.pairings) || bundle.pairings.length === 0) {
      return null
    }
    const tz = bundle.tzOffsetHours
    if (typeof tz !== 'number' || !Number.isFinite(tz)) return null
    // PGA venues are never at UTC+0. A tz of exactly 0 signals the
    // server's ?? 0 fallback for missing meta, not a real value.
    // Bail rather than risk a wrong UTC conversion.
    if (tz === 0) return null

    // Walk round-1 pairings for the minimum teeTime. parseTeeTimeMs
    // handles the local→UTC math (utcAsLocal - tz*hourMs) and returns
    // null on malformed teeTime strings — those rows just don't
    // contribute to the min.
    let minMs = Infinity
    for (const p of bundle.pairings) {
      const ms = parseTeeTimeMs(p?.teeTime, tz)
      if (typeof ms === 'number' && Number.isFinite(ms) && ms < minMs) {
        minMs = ms
      }
    }
    if (!Number.isFinite(minMs)) return null
    return new Date(minMs)
  } catch {
    return null
  }
}

// ── UFC ────────────────────────────────────────────────────────────────
async function computeUfcLockout(eventId) {
  try {
    const fights = await getUFCFights(eventId)
    if (!Array.isArray(fights) || fights.length === 0) return null
    let minMs = Infinity
    for (const f of fights) {
      const ms = f?.startTimeMs
      if (typeof ms === 'number' && Number.isFinite(ms) && ms > 0 && ms < minMs) {
        minMs = ms
      }
    }
    if (!Number.isFinite(minMs)) return null
    return new Date(minMs)
  } catch {
    return null
  }
}

// ── Daily / weekly pick'em (non-Golf, non-UFC) ─────────────────────────
async function computeDailyOrWeeklyPickemLockout(sport, startDate) {
  try {
    if (!sport || !startDate) return null
    // getGamesByDate covers ONE day (single ESPN scoreboard fetch). For
    // weekly pools, start_date is the week's first day — cutoff = min
    // startTimeMs of games on that day. If the week's earliest game is
    // actually on a LATER day, we set a slightly-late cutoff. Acceptable
    // degradation — the client-side lockoutPassed gate still catches
    // subsequent days via the start_date < today (UTC) fallback branch
    // of deriveUnlockState.
    const games = await getGamesByDate(sport, startDate)
    if (!Array.isArray(games) || games.length === 0) return null
    let minMs = Infinity
    for (const g of games) {
      const ms = g?.startTimeMs
      if (typeof ms === 'number' && Number.isFinite(ms) && ms > 0 && ms < minMs) {
        minMs = ms
      }
    }
    if (!Number.isFinite(minMs)) return null
    return new Date(minMs)
  } catch {
    return null
  }
}

// ── Bracket ────────────────────────────────────────────────────────────
async function computeBracketLockout(sport) {
  try {
    const r1Abbrs = R1_ABBRS_BY_SPORT[sport]
    const sportKey = PLAYOFF_SPORT_KEY[sport]
    if (!Array.isArray(r1Abbrs) || r1Abbrs.length === 0 || !sportKey) return null

    const url = `${API_URL}/api/espn/${sportKey}/allplayoffs`
    const res = await fetch(url, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
    if (!res.ok) return null
    const data = await res.json().catch(() => null)
    const events = Array.isArray(data?.events) ? data.events : null
    if (!events || events.length === 0) return null

    const allowed = new Set(r1Abbrs)
    let minMs = Infinity
    for (const e of events) {
      const comp = e?.competitions?.[0]
      if (!comp) continue
      const abbr = comp?.type?.abbreviation
      if (!allowed.has(abbr)) continue
      const ms = new Date(comp?.date || 0).getTime()
      if (Number.isFinite(ms) && ms > 0 && ms < minMs) {
        minMs = ms
      }
    }
    if (!Number.isFinite(minMs)) return null
    return new Date(minMs)
  } catch {
    return null
  }
}
